import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { contactInfo, services, clients, collaborations } from '../../data/mock';

const Footer = () => {
  const quickLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/services', label: 'Services' },
  { path: '/projects', label: 'Projects' },
  { path: '/clients', label: 'Clients & Partners' },
  { path: '/contact', label: 'Contact Us' }];


  const featuredServices = services.slice(0, 6);

  return (
    <footer className="bg-[#000000] border-t border-[#2A2A2A]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center mb-4 sm:mb-6">
              <img 
                src="/elemech-logo.png" 
                alt="Elemech Engineering"
                className="h-10 sm:h-14 w-auto object-contain"
              />
            </Link>
            <p className="text-[#c0c0c0] text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
              Driving Excellence and Reliability since 1988. Leading provider of comprehensive 
              CHP solutions for thermal power plants across India.
            </p>
            <div className="text-[#b00000] text-xs sm:text-sm font-medium mb-3 sm:mb-4">Since 1988</div>
            
            {/* Clients */}
            <div className="flex flex-wrap gap-2 sm:gap-4 mt-3 sm:mt-4">
              {clients.map((client, index) =>
              <div key={index} className="bg-white px-2 sm:px-4 py-1 sm:py-2 w-20 sm:w-28 h-10 sm:h-14 flex items-center justify-center">
                  <img
                  src={client.logo}
                  alt={client.name}
                  className="w-full h-full object-contain" />

                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link) =>
              <li key={link.path}>
                  <Link
                  to={link.path}
                  className="text-[#c0c0c0] hover:text-[#b00000] text-xs sm:text-sm flex items-center gap-2 transition-colors group">

                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Services */}
          <div className="hidden sm:block">
            <h4 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6">Our Services</h4>
            <ul className="space-y-2 sm:space-y-3">
              {featuredServices.map((service) =>
              <li key={service.id}>
                  <Link
                  to="/services"
                  className="text-[#c0c0c0] hover:text-[#b00000] text-xs sm:text-sm flex items-center gap-2 transition-colors group">

                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {service.title}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/services"
                  className="text-[#b00000] text-xs sm:text-sm font-medium flex items-center gap-1">

                  View All 14 Services <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6">Contact Us</h4>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <MapPin className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000] mt-0.5 flex-shrink-0" />
                <div className="text-[#c0c0c0] text-xs sm:text-sm">
                  <p>{contactInfo.address.line1}</p>
                  <p>{contactInfo.address.line2}</p>
                  <p>{contactInfo.address.city}, {contactInfo.address.state}</p>
                  <p>{contactInfo.address.country} - {contactInfo.address.pincode}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Phone className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000] flex-shrink-0" />
                <a href={`tel:${contactInfo.mobile.replace(/\s/g, '')}`} className="text-[#c0c0c0] hover:text-white text-xs sm:text-sm transition-colors">
                  {contactInfo.mobile}
                </a>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Mail className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000] flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-[#c0c0c0] hover:text-white text-xs sm:text-sm transition-colors break-all">
                  {contactInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Collaborations */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#2A2A2A]">
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
            <span className="text-[#c0c0c0] text-xs sm:text-sm">Our Collaborations:</span>
            {collaborations.map((partner, index) => (
              <span key={index} className="text-[#c0c0c0] text-xs sm:text-sm">
                {partner.name} ({partner.country})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-[#c0c0c0] text-xs sm:text-sm text-center sm:text-left">
              © {new Date().getFullYear()} ELEMECH ENGINEERING. All rights reserved. Since 1988.
            </p>
            <div className="flex gap-4 sm:gap-6">
              <Link to="/" className="text-[#c0c0c0] hover:text-[#c0c0c0] text-xs sm:text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/" className="text-[#c0c0c0] hover:text-[#c0c0c0] text-xs sm:text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;